import { supabase } from "./supabase";
import type { Article, Highlight, Tag } from "./types";

interface ExportBundle {
  articles: Article[];
  tags: Tag[];
  articleTags: Array<{ article_id: string; tag_id: string }>;
  highlights: Highlight[];
}

async function gather(): Promise<ExportBundle> {
  const [a, t, at, h] = await Promise.all([
    supabase.from("articles").select("*").order("saved_at", { ascending: false }),
    supabase.from("tags").select("*").order("name"),
    supabase.from("article_tags").select("article_id, tag_id"),
    supabase.from("highlights").select("*").order("created_at", { ascending: false }),
  ]);
  for (const r of [a, t, at, h]) if (r.error) throw r.error;
  return {
    articles: (a.data ?? []) as Article[],
    tags: (t.data ?? []) as Tag[],
    articleTags: (at.data ?? []) as Array<{ article_id: string; tag_id: string }>,
    highlights: (h.data ?? []) as Highlight[],
  };
}

function download(filename: string, content: string, mime: string) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function tagNamesForArticle(bundle: ExportBundle, articleId: string): string[] {
  const tagIds = bundle.articleTags.filter((r) => r.article_id === articleId).map((r) => r.tag_id);
  return bundle.tags.filter((t) => tagIds.includes(t.id)).map((t) => t.name);
}

export async function exportJson(): Promise<void> {
  const bundle = await gather();
  const payload = {
    exported_at: new Date().toISOString(),
    schema: 1,
    ...bundle,
  };
  download(`stash-export-${stamp()}.json`, JSON.stringify(payload, null, 2), "application/json");
}

export async function exportCsv(): Promise<void> {
  const bundle = await gather();
  const rows: string[][] = [
    ["id", "url", "title", "author", "site_name", "published_at", "saved_at", "archived_at", "read_at", "favorite", "reading_progress", "tags", "extraction_status"],
  ];
  for (const a of bundle.articles) {
    rows.push([
      a.id,
      a.url,
      a.title ?? "",
      a.author ?? "",
      a.site_name ?? "",
      a.published_at ?? "",
      a.saved_at,
      a.archived_at ?? "",
      a.read_at ?? "",
      String(a.favorite),
      String(a.reading_progress),
      tagNamesForArticle(bundle, a.id).join("|"),
      a.extraction_status,
    ]);
  }
  download(`stash-export-${stamp()}.csv`, toCsv(rows), "text/csv");
}

export async function exportHtml(): Promise<void> {
  const bundle = await gather();
  const parts: string[] = [
    "<!doctype html>",
    "<html lang='en'><head><meta charset='utf-8'>",
    "<meta name='viewport' content='width=device-width,initial-scale=1'>",
    `<title>Stash export — ${bundle.articles.length} articles</title>`,
    "<style>",
    "body{font:16px/1.6 -apple-system,Segoe UI,system-ui,sans-serif;max-width:38rem;margin:2rem auto;padding:0 1rem;color:#1a1a1a}",
    "h1{font-size:1.4rem}h2{font-size:1.1rem;margin-top:2rem}.meta{color:#666;font-size:0.85rem}.tags{color:#888;font-size:0.8rem}hr{border:0;border-top:1px solid #ddd;margin:2rem 0}mark{background:#fff3a3}",
    "</style></head><body>",
    `<h1>Stash export</h1>`,
    `<p class='meta'>${bundle.articles.length} articles, ${bundle.highlights.length} highlights. Exported ${new Date().toISOString()}.</p>`,
  ];
  for (const a of bundle.articles) {
    const tags = tagNamesForArticle(bundle, a.id);
    parts.push("<hr>");
    parts.push(`<h2>${escapeHtml(a.title ?? a.url)}</h2>`);
    parts.push(`<p class='meta'><a href='${escapeAttr(a.url)}'>${escapeHtml(a.site_name ?? a.url)}</a>${a.author ? " · " + escapeHtml(a.author) : ""}${a.published_at ? " · " + escapeHtml(a.published_at) : ""}</p>`);
    if (tags.length) parts.push(`<p class='tags'>${tags.map((t) => "#" + escapeHtml(t)).join(" ")}</p>`);
    if (a.content_html) parts.push(`<div class='article'>${a.content_html}</div>`);
    const hl = bundle.highlights.filter((h) => h.article_id === a.id);
    if (hl.length) {
      parts.push("<details><summary>Highlights</summary><ul>");
      for (const h of hl) parts.push(`<li><mark>${escapeHtml(h.exact_text)}</mark>${h.note ? " — " + escapeHtml(h.note) : ""}</li>`);
      parts.push("</ul></details>");
    }
  }
  parts.push("</body></html>");
  download(`stash-export-${stamp()}.html`, parts.join("\n"), "text/html");
}

export async function exportHighlightsMarkdown(): Promise<void> {
  const bundle = await gather();
  const lines: string[] = [`# Highlights`, ``, `Exported ${new Date().toISOString()}.`, ``];
  // Group by article.
  const byArticle = new Map<string, Highlight[]>();
  for (const h of bundle.highlights) {
    const arr = byArticle.get(h.article_id) ?? [];
    arr.push(h);
    byArticle.set(h.article_id, arr);
  }
  for (const [articleId, hls] of byArticle) {
    const article = bundle.articles.find((a) => a.id === articleId);
    lines.push(`## ${article?.title ?? article?.url ?? articleId}`);
    if (article) lines.push(`*${article.url}*`, ``);
    for (const h of hls) {
      lines.push(`> ${h.exact_text}`);
      if (h.note) lines.push(``, `**Note:** ${h.note}`);
      lines.push(``);
    }
    lines.push(``);
  }
  download(`stash-highlights-${stamp()}.md`, lines.join("\n"), "text/markdown");
}

// ---------- helpers ----------

function stamp(): string {
  return new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}
function escapeAttr(s: string): string {
  return escapeHtml(s).replace(/"/g, "&quot;");
}

function toCsv(rows: string[][]): string {
  return rows.map((r) => r.map(csvField).join(",")).join("\n");
}
function csvField(v: string): string {
  if (/[",\n]/.test(v)) return `"${v.replace(/"/g, '""')}"`;
  return v;
}
